import { useMemo, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Select } from 'antd';
import dayjs from 'dayjs';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  CalendarSkeleton,
  ChartCard,
  HBars,
  InfoIcon,
  toHex,
} from '@/components/profile';
import { DATE_FORMATS } from '@/utils/formUtils';
import { countDossier } from './employeeTiles';

const MONTHS = Array.from({ length: 12 }, (_, i) => i);

function AbsenceCalendar({ year, stats, publicHolidays, contract, tp }) {
  const holidaySet = useMemo(
    () =>
      new Set((publicHolidays || []).map((d) => dayjs(d).format('YYYY-MM-DD'))),
    [publicHolidays]
  );
  const typeByKey = useMemo(
    () => Object.fromEntries((stats?.types || []).map((x) => [x.key, x])),
    [stats]
  );
  const todayStr = dayjs().format('YYYY-MM-DD');
  const start = contract.start
    ? dayjs(contract.start).format('YYYY-MM-DD')
    : null;
  const end = contract.end ? dayjs(contract.end).format('YYYY-MM-DD') : null;

  return (
    <div
      className="flex flex-col gap-[3px]"
      role="img"
      aria-label={tp('calendarTitle', { year })}
    >
      {MONTHS.map((m) => {
        const first = dayjs(new Date(year, m, 1));
        const dim = first.daysInMonth();
        return (
          <div key={m} className="flex items-center gap-[2px]">
            <span className="w-[30px] shrink-0 text-[10px] font-semibold capitalize text-zinc-500">
              {first.format('MMM')}
            </span>
            {Array.from({ length: 31 }, (_, i) => {
              const d = i + 1;
              if (d > dim)
                return <span key={d} className="h-[13px] min-w-0 flex-1" />;
              const date = first.date(d);
              const iso = date.format('YYYY-MM-DD');
              const weekend = date.day() === 0 || date.day() === 6;
              const typeKey = stats?.days?.[iso];
              const outside = (start && iso < start) || (end && iso > end);
              let style;
              let cls = 'bg-zinc-100';
              let tip = `${date.format(DATE_FORMATS.DISPLAY)} ${tp('workday')}`;
              // A public holiday stays one even inside a longer leave (sick,
              // maternity); the leave is named in the tip.
              if (holidaySet.has(iso)) {
                const type = typeKey && typeByKey[typeKey];
                cls = 'bg-zinc-300';
                tip = `${date.format(DATE_FORMATS.DISPLAY)} ${tp('legendPublicHoliday')}${typeKey ? ` · ${type?.label || typeKey}` : ''}`;
              } else if (typeKey) {
                const type = typeByKey[typeKey];
                style = { backgroundColor: toHex(type?.color) };
                cls = '';
                tip = `${date.format(DATE_FORMATS.DISPLAY)} ${type?.label || typeKey}`;
              } else if (weekend) {
                cls = 'border border-zinc-100 bg-white';
                tip = `${date.format(DATE_FORMATS.DISPLAY)} ${tp('weekend')}`;
              } else if (outside) {
                cls = 'profile-cal-outside';
                tip = `${date.format(DATE_FORMATS.DISPLAY)} ${tp('legendOutsideContract')}`;
              }
              if (iso === todayStr) {
                cls = 'bg-red-700';
                style = undefined;
                tip = `${date.format(DATE_FORMATS.DISPLAY)} ${tp('legendToday')}`;
              }
              return (
                <span
                  key={d}
                  title={tip}
                  style={style}
                  className={`box-border h-[13px] min-w-0 flex-1 rounded-[3px] ${cls}`}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Middle column of the employee profile: the absence calendar of the year and
 * one chart that follows the selected tile. Statistics are about this employee
 * only.
 */
function ProfileStats({
  employee,
  stats,
  statsLoading,
  statsError,
  year,
  onYearChange,
  selected,
}) {
  const { t } = useTranslation(['translations', 'forms']);
  const tp = (key, opts) =>
    t(`employeeProfile.${key}`, { ns: 'translations', ...opts });

  const es = employee?.employmentSettings || {};
  const types = stats?.types || [];
  const years = useMemo(() => {
    const set = new Set([...(stats?.years || []), dayjs().year(), year]);
    return [...set].sort((a, b) => b - a);
  }, [stats, year]);

  const monthData = useMemo(
    () =>
      (stats?.byMonth || []).map((b) => ({
        ...b,
        name: dayjs(new Date(year, b.month - 1, 1)).format('MMM'),
      })),
    [stats, year]
  );

  const chart = (() => {
    if (['licno', 'isplata', 'staz', 'napomene'].includes(selected)) {
      const { done, total } = countDossier(employee);
      return (
        <ChartCard
          title={tp('dossierFill')}
          sub={tp('filledOf', { filled: done, total })}
          info={tp('infoDossier')}
        >
          <div className="h-3.5 overflow-hidden rounded bg-zinc-100">
            <div
              className="h-full bg-accent"
              style={{ width: `${(done / total) * 100}%` }}
            />
          </div>
        </ChartCard>
      );
    }

    if (selected === 'ugovor' && es.startDate) {
      const start = dayjs(es.startDate);
      const end = es.endDate ? dayjs(es.endDate) : null;
      const now = dayjs();
      const span = end ? Math.max(1, end.diff(start, 'day')) : null;
      const passed = span
        ? Math.min(100, Math.max(0, (now.diff(start, 'day') / span) * 100))
        : 100;
      return (
        <ChartCard title={tp('contractTimeline')} info={tp('infoContract')}>
          <div className="flex h-[22px] overflow-hidden rounded-md bg-zinc-100">
            <div className="bg-accent" style={{ width: `${passed}%` }} />
          </div>
          <div className="flex justify-between text-[11px] text-zinc-500">
            <span>{start.format(DATE_FORMATS.DISPLAY)}</span>
            <span>
              {end ? end.format(DATE_FORMATS.DISPLAY) : tp('unlimited')}
            </span>
          </div>
        </ChartCard>
      );
    }

    if (selected === 'odsustva' || selected === 'go') {
      const rows = (stats?.byType || [])
        .filter((x) => (selected === 'go' ? x.key === 'holidayLeave' : true))
        .map((x) => ({
          label: x.label,
          value: x.totalDays,
          color: toHex(x.color),
        }));
      if (selected === 'odsustva') {
        return (
          <ChartCard
            title={tp('absencesByType')}
            sub={String(year)}
            info={tp('infoAbsences')}
          >
            {rows.length ? (
              <HBars rows={rows} />
            ) : (
              <div className="text-sm text-zinc-500">
                {tp('noAbsences', { year })}
              </div>
            )}
          </ChartCard>
        );
      }
    }

    const shown =
      selected === 'go' ? types.filter((x) => x.key === 'holidayLeave') : types;
    return (
      <ChartCard
        title={tp('absencesByMonth')}
        sub={String(year)}
        info={tp('infoAbsences')}
      >
        {shown.length ? (
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={monthData}
                margin={{ top: 4, right: 4, left: -24, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Tooltip />
                {shown.map((x) => (
                  <Bar
                    key={x.key}
                    dataKey={x.key}
                    name={x.label}
                    stackId="a"
                    fill={toHex(x.color)}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-sm text-zinc-500">
            {tp('noAbsences', { year })}
          </div>
        )}
      </ChartCard>
    );
  })();

  return (
    <section
      aria-label={tp('calendarTitle', { year })}
      className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-auto rounded-[14px] bg-white p-3"
    >
      <div className="flex flex-col gap-2.5 rounded-[14px] border border-zinc-200 bg-white px-4 py-3.5">
        <div className="flex flex-wrap items-end justify-between gap-2.5">
          <div>
            <h2 className="m-0 text-base font-extrabold">
              {tp('calendarTitle', { year })}
              <InfoIcon text={tp('infoCalendar')} />
            </h2>
            <div
              className={`text-xs text-zinc-500 ${statsLoading ? 'invisible' : ''}`}
            >
              {tp('calendarSummary', { count: stats?.totalDays ?? 0 })}
              {(stats?.byType || [])
                .map((x) => ` · ${x.label} ${x.totalDays}`)
                .join('')}
            </div>
          </div>
          <Select
            size="small"
            value={year}
            onChange={onYearChange}
            options={years.map((y) => ({ value: y, label: y }))}
            className="w-24"
          />
        </div>

        <div className="flex flex-wrap gap-3.5 text-xs text-zinc-600">
          {types.map((x) => (
            <span key={x.key} className="inline-flex items-center gap-1.5">
              <i
                className="inline-block h-2.5 w-2.5 rounded-[3px]"
                style={{ backgroundColor: toHex(x.color) }}
              />
              {x.label}
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5">
            <i className="inline-block h-2.5 w-2.5 rounded-[3px] bg-zinc-300" />
            {tp('legendPublicHoliday')}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="profile-cal-outside inline-block h-2.5 w-2.5 rounded-[3px]" />
            {tp('legendOutsideContract')}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="inline-block h-2.5 w-2.5 rounded-[3px] bg-red-700" />
            {tp('legendToday')}
          </span>
        </div>

        {statsError && (
          <Alert type="warning" showIcon message={tp('statsUnavailable')} />
        )}
        {statsLoading ? (
          <CalendarSkeleton />
        ) : (
          <AbsenceCalendar
            year={year}
            stats={stats}
            publicHolidays={employee?.company?.publicHolidays}
            contract={{ start: es.startDate, end: es.endDate }}
            tp={tp}
          />
        )}
      </div>

      {employee && chart}
    </section>
  );
}

export default memo(ProfileStats);
